import { Component, OnInit, OnDestroy, ElementRef, ViewChild, Renderer2, Input, Output, EventEmitter, TemplateRef, ContentChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil, Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { EntityTransactionServiceAdapter } from '../../models/entity-transaction-service-adapter.interface';
import { Transaction, TransactionFilter } from '@olv/ui-shared/models/merchant-transaction.model';
import {
  type TransactionHistoryConfig,
  TransactionHistoryComponent,
  TransactionHistoryService,
} from '../transaction-history/transaction-history.component';

/**
 * Configuration interface for the entity transaction history component
 */
export interface EntityTransactionHistoryConfig<TTransaction extends Transaction = Transaction>
  extends TransactionHistoryConfig {
  /**
   * The type of entity (e.g., 'merchant', 'user', 'account')
   */
  entityType?: string;

  /**
   * Custom title for the transaction history
   */
  title?: string;

  /**
   * Whether to show the transaction details panel
   */
  showDetailsPanel?: boolean;

  /**
   * Filter options specific to the entity type
   */
  filterOptions?: {
    actionRequired?: string[];
    type?: string[];
    account?: string[];
    [key: string]: string[] | undefined;
  };
}

/**
 * Generic, reusable transaction history wrapper component that can work with any entity type.
 * This component provides a standardized interface for displaying transaction history
 * while allowing entity-specific customization through service adapters and content projection.
 *
 * @template TEntity The type of entity (e.g., Merchant, User, Account)
 * @template TTransaction The type of transaction (must extend Transaction)
 *
 * @example
 * ```html
 * <app-entity-transaction-history
 *   [entityId]="merchantId"
 *   [config]="transactionConfig"
 *   [serviceAdapter]="merchantTransactionAdapter"
 *   (transactionSelected)="onTransactionSelected($event)"
 *   (transactionUpdated)="onTransactionUpdated($event)"
 * >
 *   <!-- Entity-specific transaction details content -->
 *   <ng-template #transactionDetails let-transaction="transaction">
 *     <app-merchant-transaction-details
 *       [transaction]="transaction"
 *       (closed)="closeDetails()"
 *       (transactionUpdated)="onTransactionUpdated($event)"
 *     ></app-merchant-transaction-details>
 *   </ng-template>
 * </app-entity-transaction-history>
 * ```
 */
@Component({
  selector: 'app-entity-transaction-history',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatIconModule, TransactionHistoryComponent],
  templateUrl: './entity-transaction-history.component.html',
  styleUrls: ['./entity-transaction-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EntityTransactionHistoryComponent<TEntity = any, TTransaction extends Transaction = Transaction>
  implements OnInit, OnDestroy
{
  private route = inject(ActivatedRoute);
  private renderer = inject(Renderer2);

  /**
   * The ID of the entity to display transactions for
   */
  @Input() entityId = '';

  /**
   * Configuration object for the transaction history component
   */
  @Input() config: EntityTransactionHistoryConfig<TTransaction> = {};

  /**
   * Service adapter that bridges entity-specific services to the generic interface
   */
  @Input() serviceAdapter!: EntityTransactionServiceAdapter<TEntity, TTransaction>;

  /**
   * Optional route parameter name to extract entity ID from (defaults to 'id')
   */
  @Input() entityIdParam = 'id';

  /**
   * Whether to automatically extract entity ID from route parameters
   */
  @Input() useRouteParams = true;

  /**
   * Type to check for system or merchant
   */
  @Input() type = '';

  /**
   * When true, the inner transaction-history component hides the
   * "Exchange" entry from the Transaction Type filter dropdown.
   */
  @Input() hideExchangeType = false;

  /**
   * Optional search term applied on first load of the inner transaction history.
   */
  @Input() initialSearch = '';

  /**
   * Event emitted when a transaction is selected
   */
  @Output() transactionSelected = new EventEmitter<TTransaction>();

  /**
   * Event emitted when a transaction is updated (approved/rejected)
   */
  @Output() transactionUpdated = new EventEmitter<string>();

  /**
   * Event emitted when the inner transaction history starts or finishes loading.
   */
  @Output() loadingChange = new EventEmitter<boolean>();

  /**
   * Event emitted when the transaction details panel is closed
   */
  @Output() detailsClosed = new EventEmitter<void>();

  /**
   * Content projection for entity-specific transaction details
   */
  @ContentChild('transactionDetails')
  transactionDetailsTemplate: TemplateRef<any> | null = null;

  /**
   * Configuration for the shared transaction history component
   */
  transactionHistoryConfig: TransactionHistoryConfig = {};

  /**
   * Service adapter instance for the shared component
   */
  transactionService!: TransactionHistoryService<TTransaction>;

  /**
   * Currently selected transaction for details view
   */
  selectedTransaction: TTransaction | null = null;

  /**
   * Subject for cleanup
   */
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Initialize the transaction history configuration
    this.initializeConfig();

    // Create the service adapter wrapper
    this.createServiceAdapter();

    // Extract entity ID from route if enabled
    if (this.useRouteParams) {
      this.extractEntityIdFromRoute();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the transaction history configuration from the input config
   */
  private initializeConfig(): void {
    this.transactionHistoryConfig = {
      title: this.config.title || `${this.config.entityType || 'Entity'} Transaction History`,
      showDetailsPanel: this.config.showDetailsPanel !== false,
      filterOptions: this.config.filterOptions || {},
    };
  }

  /**
   * Create a service adapter wrapper that implements TransactionHistoryService
   */
  private createServiceAdapter(): void {
    if (!this.serviceAdapter) {
      console.error('Service adapter is required for EntityTransactionHistoryComponent');
      return;
    }

    // Create a wrapper that implements TransactionHistoryService
    this.transactionService = {
      getTransactions: (entityId: string, filter: TransactionFilter) =>
        this.serviceAdapter.getTransactions(entityId, filter),
      openSidebar: () => this.serviceAdapter.openSidebar?.(),
      closeSidebar: () => this.serviceAdapter.closeSidebar?.(),
    };
  }

  /**
   * Extract entity ID from route parameters
   */
  private extractEntityIdFromRoute(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get(this.entityIdParam);
      if (id) {
        this.entityId = id;
      } else if (this.useRouteParams) {
        console.error(`${this.entityIdParam} parameter not found in route`);
      }
    });
  }

  /**
   * Handle transaction selection from the shared component
   * @param transaction The selected transaction
   */
  onTransactionSelected(transaction: TTransaction): void {
    this.selectedTransaction = transaction;
    this.transactionSelected.emit(transaction);
  }

  /**
   * Handle when a transaction is updated (approved/rejected)
   * @param transactionId The ID of the updated transaction
   */
  onTransactionUpdated(transactionId: string): void {
    this.transactionUpdated.emit(transactionId);
  }

  /**
   * Close the transaction details panel
   */
  closeTransactionDetails(): void {
    this.selectedTransaction = null;
    this.detailsClosed.emit();

    if (this.serviceAdapter.closeSidebar) {
      this.serviceAdapter.closeSidebar();
    }
  }

  onLoadingChange(isLoading: boolean): void {
    this.loadingChange.emit(isLoading);
  }

  /**
   * Check if transaction details should be shown
   */
  get showTransactionDetails(): boolean {
    return !!(this.selectedTransaction && this.transactionDetailsTemplate);
  }

  /**
   * Get the context for the transaction details template
   */
  get transactionDetailsContext(): any {
    return {
      transaction: this.selectedTransaction,
      close: () => this.closeTransactionDetails(),
      onTransactionUpdated: (id: string) => this.onTransactionUpdated(id),
    };
  }
}
